import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  Drawer,
  AppBar,
  Toolbar,
  TextField,
  MenuItem
} from '@mui/material';
import {
  TextFields,
  Image as ImageIcon,
  Videocam,
  MusicNote,
  Crop169,
  ChevronLeft,
  ChevronRight,
  Add,
  GridView,
  ContentCopy,
  Close,
  Edit,
  OpenWith,
  Undo, 
  Redo,
  Code
} from '@mui/icons-material';
import { useImageContext } from "../context/ImageContext"; // Import context
import FilerobotImageEditor, { TABS, TOOLS } from "react-filerobot-image-editor";
import { layouts } from '../templates/templates';
import { MOBILE_DIMENSIONS, BASE_URL, BASE_URL_SHARE } from '../constants';  // Update this line
import { createTheme, ThemeProvider, FormControlLabel, Checkbox } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { slugify } from '../utils/stringUtils';

// First, add these font options near the top of the component
const InvitationEditor = () => {
  const [history, setHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [templateId, setTemplateId] = useState(null);
  const [showEventForm, setShowEventForm] = useState(true);
  const [eventDetails, setEventDetails] = useState({
    eventName: '',
    dateTime: '',
    address: '',
    contactNumber: '',
    bankName: '',
    bankAccount: '',
    bankQR: null,
    contactNumber1: '',
    contactNumber2: '',
    googleMapLink: '',
    wazeLink: '',
    googleMapEmbed: ''
  });
  const [selectedLayout, setSelectedLayout] = useState('blank');
  const [userEmail, setUserEmail] = useState('');

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  // Add this function after other handler functions
  const handleShare = async () => {
    if (!templateId) {
      alert('Please publish your template first to get a shareable link.');
      return;
    }
  
    try {
      // Fetch the template route from the server
      const response = await fetch(`${BASE_URL}/get-template-route.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          templateId: templateId
        })
      });
  
      const data = await response.json();
      
      if (data.success && data.route) {
        const shareableUrl = `${BASE_URL_SHARE}/${data.route}`;
        setShareUrl(shareableUrl);
        setShareDialogOpen(true);
      } else {
        throw new Error('Failed to get template route');
      }
    } catch (error) {
      console.error('Error getting share URL:', error);
      alert('Failed to generate sharing link. Please try again.');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        alert('Link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };

  const handleElementClick = (e, element) => {
    e.stopPropagation();
    setSelectedElement(element);
  };

  // Add click handler to deselect elements when clicking outside
  const handleEditorClick = (e) => {
    if (e.target === editorRef.current) {
      setSelectedElement(null);
    }
  };

  // Update the handleEventFormSubmit function
  const handleEventFormSubmit = () => {
    // Get the base layout elements
    const baseElements = layouts[selectedLayout].elements;
    
    // Create new elements array with populated data
    const newElements = baseElements.map(element => {
      const newElement = { ...element, id: `element-${Date.now()}-${Math.random()}` };
      
      // Populate text content based on element's identifier
      if (element.type === 'text') {
        switch (element.identifier) {
          case 'eventName':
            newElement.content = eventDetails.eventName;
            break;
          case 'dateTime':
            newElement.content = eventDetails.dateTime;
            break;
          case 'address':
            newElement.content = eventDetails.address;
            break;
          case 'contact':
            newElement.content = eventDetails.contactNumber;
            break;
          case 'bankDetails':
            newElement.content = `${eventDetails.bankName}\n${eventDetails.bankAccount}`;
            break;
          case 'contacts':
            newElement.content = eventDetails.contactNumber2 
              ? `${eventDetails.contactNumber1} / ${eventDetails.contactNumber2}`
              : eventDetails.contactNumber1;
            break;
          default:
            break;
        }
      }
      
      // Handle QR code image
      if (element.type === 'image' && element.identifier === 'bankQR' && eventDetails.bankQR) {
        newElement.src = eventDetails.bankQR;
      }
      
      return newElement;
    });
    
    setElements(newElements);
    setActiveLayout(selectedLayout);
    setShowEventForm(false);
    saveToHistory(newElements);
  };

  // Add this before the InvitationEditor component
  // Move this outside of the component and update it
  const theme = createTheme({
    typography: {
      fontFamily: 'Arial, sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Lato:ital,wght@0,400;0,700;1,400;1,700&family=Poppins:ital,wght@0,400;0,700;1,400;1,700&family=Merriweather:ital,wght@0,400;0,700;1,400;1,700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&display=swap');
        `,
      },
    },
  });

  // Add these functions after other function declarations
  const saveToHistory = (newElements) => {
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push([...newElements]);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (currentHistoryIndex > 0) {
      setCurrentHistoryIndex(currentHistoryIndex - 1);
      setElements([...history[currentHistoryIndex - 1]]);
    }
  };

  const handlePublish = async () => {
    try {
      const htmlContent = await generateHTML();
      
      // Generate route from event name
      const eventName = eventDetails.eventName || 'my-template';
      const uniqueRoute = generateEventRoute(eventName);
      
      const endpoint = templateId 
        ? `${BASE_URL}/update-template.php`
        : `${BASE_URL}/publish.php`;
  
      const requestData = {
        html: htmlContent,
        userEmail: userEmail || 'guest@ecardly.pro', // Use email instead of userId
        templateName: eventDetails.eventName || 'My Template',
        templateId: templateId,
        createdAt: new Date().toISOString(),
        elements: elements,
        route: uniqueRoute
      };
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData),
      });
  
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Received non-JSON response:', textResponse);
        throw new Error('Server returned non-JSON response');
      }
  
      const result = await response.json();
      
      if (result.success) {
        if (!templateId && result.templateId) {
          setTemplateId(result.templateId);
        }
        // Show success message with the new route
        alert(`Template published successfully!\nYour invitation is available at: ${BASE_URL_SHARE}/${uniqueRoute}`);
      } else {
        throw new Error(result.message || 'Failed to publish template');
      }
    } catch (error) {
      console.error('Error publishing template:', error);
      alert(error.message || 'Failed to publish template. Please try again.');
    }
  };

  const handleRedo = () => {
    if (currentHistoryIndex < history.length - 1) {
      setCurrentHistoryIndex(currentHistoryIndex + 1);
      setElements([...history[currentHistoryIndex + 1]]);
    }
  };
  // Update the font families array at the top of the component
  const fontFamilies = [
    'Arial',
    'Times New Roman',
    'Helvetica',
    'Georgia',
    'Verdana',
    'Roboto',
    'Open Sans',
    'Montserrat',
    'Playfair Display',
    'Lato',
    'Poppins',
    'Merriweather',
    'Dancing Script',
    'Great Vibes',
    'Pacifico'
  ];
  
  const fontStyles = [
    { value: 'normal', label: 'Normal' },
    { value: 'italic', label: 'Italic' },
    { value: 'bold', label: 'Bold' },
    { value: 'bold italic', label: 'Bold Italic' },
  ];

  const [elements, setElements] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [currentElement, setCurrentElement] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showToolbox, setShowToolbox] = useState(true);
  const [activeLayout, setActiveLayout] = useState('blank');
  const [codeVisible, setCodeVisible] = useState(false);
  const [exportFormat, setExportFormat] = useState('html');
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [currentEditingImage, setCurrentEditingImage] = useState(null);
  const editorRef = useRef(null);
  const { selectedImage, setSelectedImage } = useImageContext();

  // Add state for HTML content
  const [htmlContent, setHtmlContent] = useState('');

  const handleResize = (e, elementId) => {
    e.stopPropagation();
    const element = elements.find(el => el.id === elementId);
    const startX = e.clientX;
    const startWidth = element.width;
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(50, startWidth + deltaX);
      const aspectRatio = element.height / element.width;
      const newHeight = newWidth * aspectRatio;
      
      setElements(prevElements =>
        prevElements.map(el =>
          el.id === elementId
            ? { ...el, width: newWidth, height: newHeight }
            : el
        )
      );
    };
    
    const handleMouseUp = () => {
      saveToHistory([...elements]);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Update HTML content when needed
  React.useEffect(() => {
    if (exportFormat === 'html' && codeVisible) {
      generateHTML().then(content => {
        setHtmlContent(content);
      });
    }
  }, [exportFormat, codeVisible, elements]);

  // Add a function to handle closing the image editor
  const handleCloseImageEditor = () => {
    setImageEditorOpen(false);
    setCurrentEditingImage(null);
  };

  // Handle saving edited image from Filerobot editor
  const handleSaveImageEditor = async (editedImageData) => {
    try {
      if (editedImageData?.imageCanvas?.toDataURL) {
        const imageUrl = editedImageData.imageCanvas.toDataURL();
        
        // Update elements state with new image
        setElements(elements.map(el => {
          if (el.id === currentEditingImage.id) {
            return { ...el, src: imageUrl };
          }
          return el;
        }));
        
        // Close editor and reset current editing image
        setImageEditorOpen(false);
        setCurrentEditingImage(null);
      }
    } catch (error) {
      console.error('Error saving edited image:', error);
    }
  };

  // Handle editing an image - updated to use modal
  const handleEditImage = (element) => {
    // Set the current editing image
    setCurrentEditingImage(element);
    
    // Just close the modal without saving
    setImageEditorOpen(true);
  };

  React.useEffect(() => {
    const setZIndexForBXrui = () => {
      const elements = document.getElementsByClassName('BXrui');
      Array.from(elements).forEach(element => {
        element.style.zIndex = '1400';
      });
    };

    // Initial set
    setZIndexForBXrui();

    // Create observer to watch for new elements
    const observer = new MutationObserver(() => {
      setZIndexForBXrui();
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => observer.disconnect();
  }, []);

  // Update the preview button handler
  const handlePreview = async () => {
    try {
      const htmlContent = await generateHTML();
      // Store the HTML content in localStorage
      localStorage.setItem('previewContent', htmlContent);
      
      // Open preview in new window and inject content
      const previewWindow = window.open('/preview.html', '_blank');
      if (previewWindow) {
        previewWindow.addEventListener('load', () => {
          const container = previewWindow.document.getElementById('preview-container');
          if (container) {
            container.innerHTML = htmlContent;
          }
        });
      }
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  // Add useEffect to check for edited images when component mounts
  React.useEffect(() => {
    // Check for any edited images from localStorage (for when returning from external editor)
    const editingImageId = localStorage.getItem('editingImageId');
    const editedImage = localStorage.getItem('editedImage');
    
    if (editingImageId && editedImage) {
      // Update the element with the edited image
      setElements(elements.map(el => {
        if (el.id === editingImageId) {
          return { ...el, src: editedImage };
        }
        return el;
      }));
      
      // Clear the localStorage
      localStorage.removeItem('editingImageId');
      localStorage.removeItem('editedImage');
    }
  }, [elements]);

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create a unique filename
    const fileName = `${Date.now()}_${file.name}`;
    const uploadPath = `/uploads/${fileName}`;
    
    // In a real application, you would use FormData and fetch/axios to upload to server
    // For this demo, we'll create a local URL and simulate the upload
    const reader = new FileReader();
    reader.onload = (event) => {
      // In a production app, this would be the URL returned from your server
      // after uploading the file to the uploads folder
      const imageUrl = event.target.result;
      
      // Add the image element with the new URL
      const newElement = {
        ...elementTypes.image,
        id: `element-${Date.now()}`,
        src: imageUrl, // In production: uploadPath
        x: 100,
        y: 100
      };
      setElements([...elements, newElement]);
    };
    reader.readAsDataURL(file);
    
    // Reset the file input
    e.target.value = '';
  };
  
    // Handle video upload
    const handleVideoUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;
  
      // Create a unique filename
      const fileName = `${Date.now()}_${file.name}`;
      const uploadPath = `/uploads/${fileName}`;
  
      // In a real application, you would use FormData and fetch/axios to upload to server
      // For this demo, we'll create a local URL and simulate the upload
      const reader = new FileReader();
      reader.onload = (event) => {
        // In a production app, this would be the URL returned from your server
        // after uploading the file to the uploads folder
        const videoUrl = event.target.result;
  
        // Add the video element with the new URL
        const newElement = {
          ...elementTypes.video,
          id: `element-${Date.now()}`,
          src: videoUrl, // In production: uploadPath
          x: 100,
          y: 100
        };
        setElements([...elements, newElement]);    
      };
      reader.readAsDataURL(file);
      
      // Reset the file input
      e.target.value = '';
  };
  
  // Element types with their properties
  // Update elementTypes image and video sources
  const elementTypes = {
    text: { 
      type: 'text', 
      content: 'Double click to edit text', 
      x: 0, 
      y: 0, 
      width: 200, 
      height: 50, 
      fontSize: 16, 
      color: '#000000',
      fontFamily: 'Arial',
      fontStyle: 'normal'
    },
    image: { type: 'image', src: '/assets/canvas_placeholder.png', x: 0, y: 0, width: 300, height: 200 },
    video: { type: 'video', src: '/assets/canvas_placeholder.png', x: 0, y: 0, width: 300, height: 200 },
    music: { type: 'music', src: '', x: 10, y: 10, isPlaying: false, name: 'Background Music' },
    shape: { type: 'shape', shape: 'rectangle', x: 0, y: 0, width: 100, height: 100, color: '#6366f1' }
  };

  const getElementZIndex = (element) => {
    // Background images or shapes should always be at the back
    if ((element.type === 'image' || element.type === 'shape') && element.isBackground) {
      return 0;
    }
    // Selected element should be on top of non-selected elements
    return selectedElement?.id === element.id ? 10 : 1;
  };
  
  // Update applyLayout to include history
  const applyLayout = (layoutKey) => {
    // First clear the selection
    setSelectedElement(null);
    
    // Then update the layout with a slight delay to ensure selection is cleared first
    setTimeout(() => {
      setActiveLayout(layoutKey);
      const newElements = [...layouts[layoutKey].elements].map(el => ({
        ...el,
        id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // Ensure unique IDs
      }));
      setElements(newElements);
      saveToHistory(newElements);
    }, 10);
  };

  // Add new element to the canvas
  const addElement = (type) => {
    const newElement = {
      ...elementTypes[type],
      id: `element-${Date.now()}`,
      x: 100,
      y: 100
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
  };

  // Add this function to generate route names
  const generateEventRoute = (eventName) => {
    // Remove special characters and spaces, convert to lowercase
    const baseSlug = slugify(eventName);
    
    // Add random string to ensure uniqueness
    const uniqueId = Math.random().toString(36).substring(2, 6);
    
    return `${baseSlug}-${uniqueId}`;
  };

  // Update removeElement function
  const removeElement = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const newElements = elements.filter(element => element.id !== id);
    setElements(newElements);
    setSelectedElement(null);
    saveToHistory(newElements);
  };

  // Start dragging an element
  // Modify handleDragStart to also select the element
  // Start dragging an element
  const handleDragStart = (e, id) => {
    e.stopPropagation();
    const event = e.touches ? e.touches[0] : e;
    setIsDragging(true);
    setCurrentElement(id);
    setSelectedElement(elements.find(el => el.id === id));
    
    const elementRect = e.currentTarget.getBoundingClientRect();
    const editorRect = editorRef.current.getBoundingClientRect();
    const offsetX = event.clientX - elementRect.left;
    const offsetY = event.clientY - elementRect.top;
    
    // Store offsets in a ref or state
    e.currentTarget.dataset.offsetX = offsetX;
    e.currentTarget.dataset.offsetY = offsetY;
  };

  const handlePropertyChange = (property, value) => {
    if (!selectedElement) return;
    
    const newElements = elements.map(el => {
      if (el.id === selectedElement.id) {
        return { ...el, [property]: value };
      }
      return el;
    });
    setElements(newElements);
    setSelectedElement(prev => ({ ...prev, [property]: value }));
    saveToHistory(newElements);
  };

  // Handle dragging with throttling
  const handleDrag = React.useCallback((e) => {
    if (!isDragging || !currentElement) return;
    
    const event = e.touches ? e.touches[0] : e;
    const element = document.getElementById(currentElement);
    if (!element) return;
    
    const editorRect = editorRef.current.getBoundingClientRect();
    const offsetX = Number(element.dataset.offsetX);
    const offsetY = Number(element.dataset.offsetY);
    
    const newX = Math.max(0, Math.min(
      event.clientX - editorRect.left - offsetX,
      editorRect.width - element.offsetWidth
    ));
    const newY = Math.max(0, Math.min(
      event.clientY - editorRect.top - offsetY,
      editorRect.height - element.offsetHeight
    ));
    
    setElements(prevElements =>
      prevElements.map(el =>
        el.id === currentElement
          ? { ...el, x: newX, y: newY }
          : el
      )
    );
  }, [isDragging, currentElement]);

  // Stop dragging
  const handleDragEnd = React.useCallback(() => {
    if (isDragging) {
      saveToHistory([...elements]);
    }
    setIsDragging(false);
    setCurrentElement(null);
  }, [isDragging, elements]);

  // Set up event listeners for drag operations with cleanup
  // Update the event listeners in useEffect
  React.useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e) => {
      e.preventDefault(); // Prevent scrolling while dragging
      handleDrag(e);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDrag, handleDragEnd]);

  // Edit text element on double click
  // Update handleDoubleClick for text editing
  const handleDoubleClick = (id) => {
    const element = elements.find(el => el.id === id);
    if (element.type === 'text') {
      const newContent = prompt('Edit text:', element.content);
      if (newContent !== null) {
        const newElements = elements.map(el => {
          if (el.id === id) {
            return { ...el, content: newContent };
          }
          return el;
        });
        setElements(newElements);
        saveToHistory(newElements);
      }
    }
  };

   // Add this function to save assets and get their URLs
   const saveAssetToFile = async (dataUrl, type) => {
    const fileName = `asset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const extension = type === 'image' ? '.png' : type === 'video' ? '.mp4' : '.mp3';
    
    try {
      const response = await fetch(`${BASE_URL}/save-asset.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: dataUrl,
          fileName: fileName + extension,
          type: type
        }),
      });

      if (response.ok) {
        const { filePath } = await response.json();
        return filePath; // Returns /uploads/filename.extension
      }
      throw new Error('Failed to save asset');
    } catch (error) {
      console.error('Error saving asset:', error);
      return null;
    }
  };

   // Generate HTML output
   const generateHTML = async () => {
    // First, save all assets and get their URLs
    const savedElements = await Promise.all(
      elements.map(async (element) => {
        if (['image', 'video', 'music'].includes(element.type) && element.src) {
          // If the source is a data URL, save it and get the new path
          if (element.src.startsWith('data:')) {
            const savedPath = await saveAssetToFile(element.src, element.type);
            // Convert local path to full URL
            return { 
              ...element, 
              src: savedPath ? `https://ecardly.pro${savedPath}` : element.src 
            };
          }
          // If it's already a relative path, add the domain
          else if (element.src.startsWith('/')) {
            return {
              ...element,
              src: `https://ecardly.pro${element.src}`
            };
          }
        }
        return element;
      })
    );

    let html = `<!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
      <title>Digital Invitation</title>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Lato:ital,wght@0,400;0,700;1,400;1,700&family=Poppins:ital,wght@0,400;0,700;1,400;1,700&family=Merriweather:ital,wght@0,400;0,700;1,400;1,700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        :root {
          --card-width: ${MOBILE_DIMENSIONS.width}px;
          --card-height: ${MOBILE_DIMENSIONS.height}px;
          --scale: 1;
        }
        body {
          font-family: Arial, sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background-color: #f1f5f9;
          margin: 0;
          padding: 0;
        }
        .invitation-card {
          position: relative;
          width: var(--card-width);
          height: var(--card-height);
          background-color: white;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transform-origin: top center;
        }
        @media screen and (max-width: ${MOBILE_DIMENSIONS.width}px) {
          :root {
            --scale: calc(100vw / ${MOBILE_DIMENSIONS.width});
          }
          .invitation-card {
            transform: scale(var(--scale));
          }
        }
        .element {
          position: absolute;
          user-select: none;
        }
        .text-element {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
        .shape-element {
          border-radius: 0;
        }
        .shape-element.circle {
          border-radius: 50%;
        }
        .music-element {
          display: flex;
          align-items: center;
          background-color: #f3f4f6;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 14px;
        }
        .music-element svg {
          margin-right: 8px;
        }
        video.element {
          object-fit: cover;
          background-color: black;
        }
      </style>
    </head>
    <body>
      <div class="invitation-card">`;

    // Add elements to HTML
    savedElements.forEach(element => {
      switch(element.type) {
        case 'text':
          html += `    <div class="element text-element" style="left: ${element.x}px; top: ${element.y}px; width: ${element.width}px; height: ${element.height}px; font-size: ${element.fontSize}px; color: ${element.color}; font-family: ${element.fontFamily || 'Arial'}, Arial, sans-serif; font-weight: ${element.fontStyle?.includes('bold') ? 700 : 400}; font-style: ${element.fontStyle?.includes('italic') ? 'italic' : 'normal'};">${element.content}</div>\n`;
          break;
        case 'image':
          if (element.src) {
            html += `    <img class="element" src="${element.src}" alt="Invitation image" style="left: ${element.x}px; top: ${element.y}px; width: ${element.width}px; height: ${element.height}px; object-fit: cover;">\n`;
          }
          break;
        case 'video':
          if (element.src) {
            html += `    <video class="element" controls style="left: ${element.x}px; top: ${element.y}px; width: ${element.width}px; height: ${element.height}px;">
        <source src="${element.src}" type="video/mp4">
        Your browser does not support the video tag.
      </video>\n`;
          }
          break;
        case 'music':
          html += `    <div class="element music-element" style="left: ${element.x}px; top: ${element.y}px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M9 18V5l12-2v13"></path>
        <circle cx="6" cy="18" r="3"></circle>
        <circle cx="18" cy="16" r="3"></circle>
      </svg>
      ${element.name}
    </div>\n`;
          break;
        case 'shape':
          html += `    <div class="element shape-element${element.shape === 'circle' ? ' circle' : ''}" style="left: ${element.x}px; top: ${element.y}px; width: ${element.width}px; height: ${element.height}px; background-color: ${element.color};"></div>\n`;
          break;
        default:
          break;
      }
    });

    html += `  </div>
</body>
</html>`;

    return html;
  };

  // Generate CSS output
  const generateCSS = () => {
    return `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
body {
  font-family: Arial, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f1f5f9;
}
.invitation-card {
  position: relative;
  width: 800px;
  height: 600px;
  background-color: white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.element {
  position: absolute;
  user-select: none;
}
.text-element {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.shape-element {
  border-radius: 0;
}
.shape-element.circle {
  border-radius: 50%;
}
.music-element {
  display: flex;
  align-items: center;
  background-color: #f3f4f6;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
}
.music-element svg {
  margin-right: 8px;
}
video.element {
  object-fit: cover;
  background-color: black;
}
@media print {
  body {
    background-color: white;
  }
  .invitation-card {
    box-shadow: none;
  }
}`;
  };

  // Copy code to clipboard
  const copyToClipboard = () => {
    const textToCopy = exportFormat === 'html' ? generateHTML() : generateCSS();
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        alert('Copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Set up event listeners for drag operations
  React.useEffect(() => {
    const handleMouseMove = (e) => {
      handleDrag(e);
    };
    
    const handleMouseUp = () => {
      handleDragEnd();
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, currentElement, elements]);

  return (
    <ThemeProvider theme={theme}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Lato:ital,wght@0,400;0,700;1,400;1,700&family=Poppins:ital,wght@0,400;0,700;1,400;1,700&family=Merriweather:ital,wght@0,400;0,700;1,400;1,700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&display=swap');
        `}
      </style>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Header */}
        <AppBar position="static" color="default" elevation={1}>
        <Toolbar 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              gap: 1,
              flexWrap: 'wrap',
              p: { xs: 1, sm: 2 }
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton 
                onClick={handleUndo} 
                disabled={currentHistoryIndex <= 0}
                size="small"
                sx={{ /* existing styles */ }}
              >
                <Undo fontSize="small" />
              </IconButton>
              <IconButton 
                onClick={handleRedo}
                disabled={currentHistoryIndex >= history.length - 1}
                size="small"
                sx={{ /* existing styles */ }}
              >
                <Redo fontSize="small" />
              </IconButton>
              <Button
                variant="contained"
                onClick={() => setCodeVisible(!codeVisible)}
                sx={{
                  ml: 1,
                  bgcolor: 'grey.800',
                  '&:hover': {
                    bgcolor: 'grey.900'
                  }
                }}
                startIcon={<Code />}
                size="small"
              >
                {codeVisible ? 'Hide Code' : 'Show Code'}
              </Button>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' },
              flexWrap: 'wrap'
            }}>
              <Button
                variant="outlined"
                startIcon={<Videocam />}
                onClick={handlePreview}
                size="small" // Changed from object to string
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Preview
              </Button>
              <Button 
                variant="outlined" 
                color="success"
                size="small"
                onClick={handlePublish}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Publish
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                size="small"
                onClick={handleShare}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Share
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        {/* Main content */}

        {/* Code export section */}
        {codeVisible && (
          <Paper sx={{ bgcolor: 'grey.900', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={exportFormat === 'html' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setExportFormat('html')}
                  sx={{ color: 'white' }}
                >
                  HTML
                </Button>
                <Button
                  variant={exportFormat === 'css' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setExportFormat('css')}
                  sx={{ color: 'white' }}
                >
                  CSS
                </Button>
              </Box>
              <Button
                startIcon={<ContentCopy />}
                size="small"
                onClick={copyToClipboard}
                sx={{ color: 'white' }}
              >
                Copy
              </Button>
            </Box>
            <TextField
              multiline
              fullWidth
              rows={8}
              value={exportFormat === 'html' ? htmlContent : generateCSS()}
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: 'monospace',
                  bgcolor: 'grey.900',
                  color: '#4ade80',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.800'
                  }
                }
              }}
            />
          </Paper>
        )}

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Toolbox */}
          <Drawer
            variant="persistent"
            anchor="left"
            open={showToolbox}
            sx={{
              width: showToolbox ? { xs: '100%', sm: 240 } : 0,
              '& .MuiDrawer-paper': {
                width: { xs: '90%', sm: 240 }, // Reduced width on mobile
                position: { xs: 'fixed', sm: 'relative' },
                border: 'none',
                transform: showToolbox ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
                zIndex: { xs: 1, sm: 1 },
                mt: { xs: 8, sm: 1 }, // Add margin top on mobile
                ml: { xs: '50px', sm: 0 }, // Add margin left on mobile 
                borderRadius: { xs: '8px', sm: 0 }, // Add rounded corners on mobile
                maxHeight: { xs: 'calc(100% - 32px)', sm: '100%' }, // Adjust height on mobile
                boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.1)', sm: 'none' } // Add shadow on mobile
              }
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Layouts</Typography>
              <Grid container spacing={1} direction="column">
                {Object.keys(layouts).map(layoutKey => (
                  <Grid item key={layoutKey}>
                    <Button
                      fullWidth
                      variant={activeLayout === layoutKey ? 'contained' : 'outlined'}
                      onClick={() => applyLayout(layoutKey)}
                      startIcon={<GridView />}
                      size="small"
                    >
                      {layouts[layoutKey].name}
                    </Button>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Add Elements</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => addElement('text')}
                    startIcon={<TextFields />}
                  >
                    Text
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => document.getElementById('image-upload').click()}
                    startIcon={<ImageIcon />}
                  >
                    Image
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <input
                    type="file"
                    id="video-upload"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={handleVideoUpload}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => document.getElementById('video-upload').click()}
                    startIcon={<Videocam />}
                  >
                    Video
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => addElement('music')}
                    startIcon={<MusicNote />}
                  >
                    Music
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => addElement('shape')}
                    startIcon={<Crop169 />}
                  >
                    Shape
                  </Button>
                </Grid>
              </Grid>

              {selectedElement && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Properties</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {selectedElement && (selectedElement.type === 'image' || selectedElement.type === 'shape') && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedElement.isBackground || false}
                              onChange={(e) => handlePropertyChange('isBackground', e.target.checked)}
                            />
                          }
                          label="Use as background"
                        />
                      </Grid>
                    )}
                    {selectedElement.type === 'text' && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Text Content"
                            value={selectedElement.content}
                            onChange={(e) => handlePropertyChange('content', e.target.value)}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            label="Font Family"
                            value={selectedElement.fontFamily || 'Arial'}
                            onChange={(e) => handlePropertyChange('fontFamily', e.target.value)}
                            size="small"
                          >
                            {fontFamilies.map((font) => (
                              <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                                {font}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            select
                            fullWidth
                            label="Font Style"
                            value={selectedElement.fontStyle || 'normal'}
                            onChange={(e) => handlePropertyChange('fontStyle', e.target.value)}
                            size="small"
                          >
                            {fontStyles.map((style) => (
                              <MenuItem key={style.value} value={style.value}>
                                {style.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            type="number"
                            fullWidth
                            label="Font Size"
                            value={selectedElement.fontSize}
                            onChange={(e) => handlePropertyChange('fontSize', Number(e.target.value))}
                            size="small"
                            InputProps={{ inputProps: { min: 8, max: 72 } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            type="color"
                            label="Text Color"
                            value={selectedElement.color}
                            onChange={(e) => handlePropertyChange('color', e.target.value)}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </>
              )}
            </Box>
          </Drawer>

          {/* Toggle toolbox button - Updated to follow toolbox size */}
          <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
            {/* Toggle button */}
            <Box
            sx={{
              position: 'absolute',
              zIndex: 2,
              left: showToolbox ? { xs: 0, sm: 30 } : 0, // Move 8px right when toolbox is open
              top: { xs: 16, sm: 64 },
              transform: showToolbox ? 'translateX(-100%)' : 'translateX(0)',
              transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
            }}
          >
            <IconButton
              onClick={() => setShowToolbox(!showToolbox)}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: {
                  xs: showToolbox ? '8px 0 0 8px' : '0 8px 8px 0',
                  sm: '0 8px 8px 0'
                },
                boxShadow: 2,
                width: 32,
                height: 32,
                '&:hover': { 
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderColor: 'primary.main'
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ChevronLeft 
                sx={{
                  fontSize: 20,
                  transition: 'transform 0.2s ease-in-out',
                  transform: showToolbox ? 'rotate(0deg)' : 'rotate(180deg)'
                }}
              />
            </IconButton>
          </Box>
          
          <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: { xs: 1, sm: 2 },
                bgcolor: 'inherit',
                mt: { xs: showToolbox ? 0 : 'auto', sm: 0 }
              }}
              onMouseMove={handleDrag}
              onMouseUp={handleDragEnd}
            >
            <Paper
              ref={editorRef}
              onClick={handleEditorClick}
              sx={{
                position: 'relative',
                width: MOBILE_DIMENSIONS.width,
                height: MOBILE_DIMENSIONS.height,
                mx: 'auto',
                overflow: 'hidden',
                bgcolor: 'white',
              }}
            >
              {elements.map((element, index) => (
                <Box
                    key={`${element.id}-${index}`}
                    id={element.id}
                    sx={{
                      position: 'absolute',
                      left: `${element.x}px`,
                      top: `${element.y}px`,
                      width: `${element.width}px`,
                      height: `${element.height}px`,
                      cursor: 'move',
                      border: selectedElement?.id === element.id ? '1px solid #2196f3' : '1px solid transparent',
                      '&:hover': {
                        border: '1px solid #2196f3',
                        '& .element-controls': {
                          opacity: 1
                        }
                      },
                      '& .element-controls': {
                        opacity: selectedElement?.id === element.id ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out'
                      },
                      zIndex: getElementZIndex(element),
                      pointerEvents: 'all'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleElementClick(e, element);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent default browser behavior
                    e.stopPropagation();
                    handleDragStart(e, element.id);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    handleDoubleClick(element.id);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleDragStart(e, element.id);
                  }}
                >
                  {element.type === 'text' && (                
                     <Typography
                     sx={{
                       width: '100%',
                       height: '100%',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       fontSize: `${element.fontSize}px`,
                       color: element.color,
                       fontFamily: `${element.fontFamily}, Arial, sans-serif !important`,
                       fontWeight: element.fontStyle?.includes('bold') ? 700 : 400,
                       fontStyle: element.fontStyle?.includes('italic') ? 'italic' : 'normal',
                       textAlign: 'center',
                       userSelect: 'none',
                       WebkitFontSmoothing: 'antialiased',
                       MozOsxFontSmoothing: 'grayscale',
                     }}
                   >
                     {element.content}
                   </Typography>
                  )}

                  {element.type === 'image' && element.src && (
                    <Box
                      component="img"
                      src={element.src}
                      alt="Invitation element"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        userSelect: 'none'
                      }}
                    />
                  )}

                  {element.type === 'image' && (
                    <IconButton
                    className="element-controls"
                    size="small"
                    onClick={() => handleEditImage(element)}
                    sx={{
                      position: 'absolute',
                      top: -16,
                      left: -16,
                      bgcolor: 'primary.main',
                      color: 'white',
                      opacity: selectedElement?.id === element.id ? 1 : 0,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        opacity: 1
                      }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  )}

                  {element.type === 'image' && (
                    <IconButton
                      size="small"
                      onMouseDown={(e) => handleResize(e, element.id)}
                      sx={{
                        position: 'absolute',
                        left: -8,
                        bottom: -8,
                        bgcolor: 'primary.main',
                        color: 'white',
                        opacity: selectedElement?.id === element.id ? 1 : 0,
                        '&:hover': {
                          opacity: 1,
                          bgcolor: 'primary.dark'
                        }
                      }}
                    >
                      <OpenWith fontSize="small" />
                    </IconButton>
                  )}

                  {element.type === 'shape' && (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: element.color,
                        borderRadius: element.shape === 'circle' ? '50%' : 0,
                        userSelect: 'none'
                      }}
                    />
                  )}

                  {element.type === 'video' && (
                    <Box
                      component="video"
                      src={element.src}
                      controls
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        userSelect: 'none',
                        bgcolor: 'black'
                      }}
                    >
                      Your browser does not support the video tag.
                    </Box>
                  )}

                  {element.type === 'music' && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#f3f4f6',
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        userSelect: 'none'
                      }}
                    >
                      <MusicNote sx={{ mr: 1 }} />
                      <Typography>{element.name}</Typography>
                    </Box>
                  )}

                  <IconButton
                      size="small"
                      onClick={(e) => removeElement(e, element.id)}
                      sx={{
                        position: 'absolute',
                        top: -16,
                        right: -16,
                        bgcolor: 'error.main',
                        color: 'white',
                        opacity: selectedElement?.id === element.id ? 1 : 0,
                        '&:hover': {
                          bgcolor: 'error.dark',
                          opacity: 1
                        }
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                </Box>
              ))}

              {elements.length === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <IconButton
                      sx={{
                        mb: 2,
                        bgcolor: 'primary.50',
                        '&:hover': { bgcolor: 'primary.100' }
                      }}
                    >
                      <Add sx={{ color: 'primary.main' }} />
                    </IconButton>
                    <Typography color="primary.main">
                      Choose a layout or add elements to get started.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
        {imageEditorOpen && currentEditingImage && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 1300,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2,
                position: 'relative',
                zIndex: 1300
              }}
            >
              <IconButton
                onClick={handleCloseImageEditor}
                sx={{
                  color: 'white',
                  bgcolor: 'error.main',
                  '&:hover': { bgcolor: 'error.dark' }
                }}
              >
                <Close />
              </IconButton>
            </Box>
            
            <Box sx={{ flex: 1, position: 'relative' }}>
              <FilerobotImageEditor
                source={currentEditingImage.src}
                onSave={handleSaveImageEditor}
                annotationsCommon={{ fill: "#ff0000" }}
                Text={{
                  text: "Filerobot...",
                  fonts: [
                    "Arial",
                    "Verdana",
                    "Times New Roman",
                    "Courier New",
                    "Comic Sans MS",
                    "Georgia",
                    "Impact",
                    "Tahoma",
                    "Trebuchet MS",
                    "Lucida Sans",
                    "Poppins",
                    "Roboto",
                    "Lato",
                    "Montserrat",
                    "Open Sans",
                  ],
                }}
                Rotate={{ angle: 90, componentType: "slider" }}
                Adjust={{ removeBackground: true }}
                tabsIds={[
                  TABS.ADJUST,
                  TABS.ANNOTATE,
                  TABS.WATERMARK,
                  TABS.RESIZE,
                  TABS.CROP,
                  TABS.FILTERS,
                  TABS.FINE_TUNE,
                  TABS.FOCUS_POINT,
                ]}
                defaultTabId={TABS.ANNOTATE}
                defaultToolId={TOOLS.TEXT}
                toolsIds={[
                  TOOLS.TEXT,
                  TOOLS.IMAGE,
                  TOOLS.CROP,
                  TOOLS.RESIZE,
                  TOOLS.WATERMARK,
                  TOOLS.DRAW,
                  TOOLS.SHAPES,
                  TOOLS.ICONS,
                  TOOLS.BACKGROUND,
                  TOOLS.REMOVE_BG,
                ]}
              />
            </Box>
          </Box>
        )}
      </Box>
      </Box>
      <Dialog 
        open={showEventForm} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2 
        }}>
          Event Details
          <IconButton 
            size="small" 
            onClick={() => setShowEventForm(false)}
            sx={{ 
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ mt: 1, mb: 2 }}>  {/* Added spacing box */}
                <TextField
                  select
                  fullWidth
                  label="Select Layout"
                  value={selectedLayout}
                  onChange={(e) => setSelectedLayout(e.target.value)}
                >
                  {Object.keys(layouts).map((layoutKey) => (
                    <MenuItem key={layoutKey} value={layoutKey}>
                      {layouts[layoutKey].name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Name"
                value={eventDetails.eventName}
                onChange={(e) => setEventDetails({...eventDetails, eventName: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date / Time"
                value={eventDetails.dateTime}
                onChange={(e) => setEventDetails({...eventDetails, dateTime: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={eventDetails.address}
                onChange={(e) => setEventDetails({...eventDetails, address: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Number"
                value={eventDetails.contactNumber}
                onChange={(e) => setEventDetails({...eventDetails, contactNumber: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                value={eventDetails.bankName}
                onChange={(e) => setEventDetails({...eventDetails, bankName: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={eventDetails.bankAccount}
                onChange={(e) => setEventDetails({...eventDetails, bankAccount: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <input
                type="file"
                accept="image/*"
                id="bank-qr"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setEventDetails({...eventDetails, bankQR: event.target.result});
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <Button
                fullWidth
                variant="outlined"
                onClick={() => document.getElementById('bank-qr').click()}
              >
                Upload Bank QR Code
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number 1"
                value={eventDetails.contactNumber1}
                onChange={(e) => setEventDetails({...eventDetails, contactNumber1: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number 2 (optional)"
                value={eventDetails.contactNumber2}
                onChange={(e) => setEventDetails({...eventDetails, contactNumber2: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Map Link"
                value={eventDetails.googleMapLink}
                onChange={(e) => setEventDetails({...eventDetails, googleMapLink: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Waze Link"
                value={eventDetails.wazeLink}
                onChange={(e) => setEventDetails({...eventDetails, wazeLink: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Map Iframe Embed"
                multiline
                rows={3}
                value={eventDetails.googleMapEmbed}
                onChange={(e) => setEventDetails({...eventDetails, googleMapEmbed: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ mt: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleEventFormSubmit}
            fullWidth
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Share Invitation
          <IconButton
            onClick={() => setShareDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              value={shareUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Button
                    variant="contained"
                    onClick={handleCopyLink}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    Copy
                  </Button>
                ),
              }}
            />
          </Box>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank')}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
            >
              Share on WhatsApp
            </Button>
            <Button
              variant="contained"
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
              sx={{ bgcolor: '#1877F2', '&:hover': { bgcolor: '#0E5A9C' } }}
            >
              Share on Facebook
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

export default InvitationEditor;

